import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Send, Plus, X, Smartphone, Mail,
  CheckCircle, AlertCircle, Loader2, Copy, Eye
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const AdminTesterInvites = () => {
  const navigate = useNavigate();
  const [testingLink, setTestingLink] = useState('');
  const [emails, setEmails] = useState(['']);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const addEmailField = () => {
    setEmails([...emails, '']);
  };

  const removeEmailField = (index) => {
    if (emails.length > 1) {
      setEmails(emails.filter((_, i) => i !== index));
    }
  };

  const updateEmail = (index, value) => {
    const newEmails = [...emails];
    newEmails[index] = value;
    setEmails(newEmails);
  };

  const handlePasteEmails = (e) => {
    const pastedText = e.clipboardData.getData('text');
    // Check if pasted text contains multiple emails (comma, newline, or semicolon separated)
    if (pastedText.includes(',') || pastedText.includes('\n') || pastedText.includes(';')) {
      e.preventDefault();
      const pastedEmails = pastedText
        .split(/[,;\n]+/)
        .map(email => email.trim())
        .filter(email => email && email.includes('@'));
      
      if (pastedEmails.length > 0) {
        setEmails(pastedEmails);
        toast.success(`Imported ${pastedEmails.length} emails`);
      }
    }
  };

  const sendInvites = async () => {
    // Validate - filter for valid emails
    const validEmails = emails.filter(e => e.trim() && e.includes('@'));
    
    console.log('All emails entered:', emails);
    console.log('Valid emails to send:', validEmails);
    console.log(`Filtered: ${emails.length} -> ${validEmails.length}`);
    
    if (validEmails.length === 0) {
      toast.error('Please add at least one valid email');
      return;
    }
    if (!testingLink.trim()) {
      toast.error('Please enter the Play Store testing link');
      return;
    }

    setSending(true);
    try {
      const response = await api.post('/admin/send-tester-invite', {
        emails: validEmails,
        testing_link: testingLink
      });

      console.log('API Response:', response.data);

      if (response.data.success) {
        setResults(response.data);
        if (response.data.errors && response.data.errors.length > 0) {
          toast.warning(`Sent ${response.data.sent}/${response.data.total} - some failed`);
        } else {
          toast.success(`Sent ${response.data.sent} invites!`);
        }
      } else {
        toast.error('Failed to send invites');
      }
    } catch (error) {
      console.error('Send error:', error);
      toast.error(error.response?.data?.detail || 'Failed to send invites');
    } finally {
      setSending(false);
    }
  };

  const validEmailCount = emails.filter(e => e.trim() && e.includes('@')).length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-[#3DDC84]" />
              Android Tester Invites
            </h1>
            <p className="text-sm text-gray-500">Send beautiful beta test invitations via email</p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6 space-y-6">
        
        {/* Testing Link */}
        <div className="bg-white rounded-2xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Copy className="w-5 h-5 text-[#1C3A7A]" />
            Play Store Testing Link
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Get this from Google Play Console → Internal Testing → Testers → "Copy link"
          </p>
          <Input
            type="url"
            placeholder="https://play.google.com/apps/internaltest/..."
            value={testingLink}
            onChange={(e) => setTestingLink(e.target.value)}
            className="font-mono text-sm"
          />
        </div>

        {/* Email List */}
        <div className="bg-white rounded-2xl shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#1C3A7A]" />
              Tester Emails
              {validEmailCount > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-[#1C3A7A] text-white text-xs rounded-full">
                  {validEmailCount}
                </span>
              )}
            </h2>
            <Button variant="outline" size="sm" onClick={addEmailField}>
              <Plus className="w-4 h-4 mr-1" />
              Add Email
            </Button>
          </div>
          
          <p className="text-sm text-gray-500 mb-4">
            Tip: Paste a comma-separated list of emails to bulk import
          </p>

          <div className="space-y-3">
            {emails.map((email, index) => (
              <div key={index} className="flex gap-2">
                <Input
                  type="email"
                  placeholder="tester@example.com"
                  value={email}
                  onChange={(e) => updateEmail(index, e.target.value)}
                  onPaste={index === 0 ? handlePasteEmails : undefined}
                  className={email && !email.includes('@') ? 'border-red-300' : ''}
                />
                {emails.length > 1 && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeEmailField(index)}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Preview */}
        <div className="bg-white rounded-2xl shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Eye className="w-5 h-5 text-[#1C3A7A]" />
              Email Preview
            </h2>
            <Button variant="outline" size="sm" onClick={() => setShowPreview(!showPreview)}>
              {showPreview ? 'Hide' : 'Show'} Preview
            </Button>
          </div>
          
          {showPreview && (
            <div className="border rounded-xl overflow-hidden">
              <div className="bg-gradient-to-r from-[#1C3A7A] to-[#2a4a8a] p-8 text-center">
                <h3 className="text-white text-2xl font-bold">You're Invited!</h3>
                <p className="text-[#FAB710] text-lg font-semibold mt-2">Be one of the first to try RecoveryMatch on Android</p>
              </div>
              <div className="p-6 bg-white">
                <div className="text-center mb-6">
                  <span className="inline-block bg-gradient-to-r from-[#3DDC84] to-[#2bc270] text-white text-sm font-semibold px-5 py-2 rounded-full tracking-wide">
                    ANDROID BETA TESTER
                  </span>
                </div>
                <p className="text-gray-600 text-center mb-6">
                  Great news! You've been selected as one of our exclusive beta testers for <strong className="text-[#1C3A7A]">RecoveryMatch on Android</strong>. Your feedback will help us build a better app for the entire recovery community.
                </p>
                
                {/* What's New Box */}
                <div className="bg-gradient-to-r from-[#1C3A7A] to-[#2a4a8a] rounded-xl p-5 mb-6">
                  <h4 className="text-[#FAB710] font-bold text-center mb-3">What's New in Version 1.0.2</h4>
                  <div className="space-y-2 text-white text-sm">
                    <p><span className="text-[#FAB710] mr-2">✓</span>More inclusive gender & relationship options</p>
                    <p><span className="text-[#FAB710] mr-2">✓</span>Enhanced profiles with Core Values & Recovery Journey</p>
                    <p><span className="text-[#FAB710] mr-2">✓</span>Daily Affirmations to support your journey</p>
                    <p><span className="text-[#FAB710] mr-2">✓</span>Performance improvements & bug fixes</p>
                  </div>
                </div>
                
                <div className="text-center mb-6">
                  <span className="inline-block bg-gradient-to-r from-[#3DDC84] to-[#2bc270] text-white font-bold px-8 py-4 rounded-full shadow-lg">
                    Join the Beta on Google Play
                  </span>
                </div>
                
                {/* Steps */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3">
                    <span className="w-7 h-7 bg-[#FAB710] text-[#1C3A7A] rounded-full flex items-center justify-center font-bold text-sm">1</span>
                    <span className="text-gray-700"><strong>Open the link above</strong> on your Android device</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="w-7 h-7 bg-[#FAB710] text-[#1C3A7A] rounded-full flex items-center justify-center font-bold text-sm">2</span>
                    <span className="text-gray-700"><strong>Sign in with this email</strong> to access the beta</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="w-7 h-7 bg-[#FAB710] text-[#1C3A7A] rounded-full flex items-center justify-center font-bold text-sm">3</span>
                    <span className="text-gray-700"><strong>Accept the invitation</strong> and install from Google Play</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="w-7 h-7 bg-[#FAB710] text-[#1C3A7A] rounded-full flex items-center justify-center font-bold text-sm">4</span>
                    <span className="text-gray-700"><strong>Start exploring</strong> and share your feedback!</span>
                  </div>
                </div>
                
                {/* Feedback Box */}
                <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-4 text-center">
                  <h5 className="text-[#1C3A7A] font-bold mb-1">Your Feedback Matters</h5>
                  <p className="text-gray-500 text-sm">Found a bug or have a suggestion? Reply to this email!</p>
                </div>
              </div>
              <div className="bg-gray-50 border-t p-4 text-center">
                <p className="text-[#1C3A7A] font-semibold">RecoveryMatch</p>
                <p className="text-gray-400 text-xs">Healthy connections for people committed to recovery.</p>
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        {results && (
          <div className="bg-white rounded-2xl shadow-sm border p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Results
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-green-50 rounded-xl text-center">
                <p className="text-3xl font-bold text-green-600">{results.sent}</p>
                <p className="text-sm text-green-700">Sent Successfully</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-xl text-center">
                <p className="text-3xl font-bold text-gray-600">{results.total}</p>
                <p className="text-sm text-gray-500">Total Emails</p>
              </div>
            </div>
            {results.errors && results.errors.length > 0 && (
              <div className="mt-4 p-4 bg-red-50 rounded-xl">
                <p className="font-medium text-red-700 mb-2">Failed to send:</p>
                {results.errors.map((err, i) => (
                  <p key={i} className="text-sm text-red-600">{err.email}: {err.error}</p>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Send Button */}
        <Button
          onClick={sendInvites}
          disabled={sending || validEmailCount === 0 || !testingLink.trim()}
          className="w-full bg-[#3DDC84] hover:bg-[#2bc270] text-white py-6 text-lg font-semibold rounded-xl"
        >
          {sending ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Sending Invites...
            </>
          ) : (
            <>
              <Send className="w-5 h-5 mr-2" />
              Send {validEmailCount} Invite{validEmailCount !== 1 ? 's' : ''}
            </>
          )}
        </Button>

      </div>
    </div>
  );
};

export default AdminTesterInvites;
